import React, {
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react"
import PropTypes from "prop-types"

function waitForTurnstile({ timeoutMs = 10000, intervalMs = 200 } = {}) {
  if (typeof window === "undefined") return Promise.resolve()
  if (window.turnstile && typeof window.turnstile.render === "function") {
    return Promise.resolve()
  }
  return new Promise((resolve, reject) => {
    let done = false
    let i = 0
    let t = 0
    const finish = (fn) => {
      if (done) return
      done = true
      window.removeEventListener("cf:turnstile:load", onLoad)
      if (i) clearInterval(i)
      if (t) clearTimeout(t)
      fn()
    }
    const onLoad = () => {
      if (window.turnstile && typeof window.turnstile.render === "function") {
        finish(resolve)
      }
    }
    window.addEventListener("cf:turnstile:load", onLoad, { once: true })
    i = setInterval(() => {
      if (window.turnstile && typeof window.turnstile.render === "function") {
        finish(resolve)
      }
    }, intervalMs)
    t = setTimeout(() => finish(() => reject(new Error("turnstile_load_timeout"))), timeoutMs)
  })
}

const CustomTurnstile = forwardRef(function CustomTurnstile(
  {
    siteKey = "",
    onTokenChange,
    onTokenExpire,
    formSubmitted,
    size = "invisible",
    theme = "auto",
    action,
  },
  ref
) {
  const containerRef = useRef(null)
  const widgetIdRef = useRef(null)
  const unmountedRef = useRef(false)
  const onTokenChangeRef = useRef(onTokenChange)
  const onTokenExpireRef = useRef(onTokenExpire)
  const retryingRef = useRef(false)

  useEffect(() => {
    onTokenChangeRef.current = onTokenChange
  }, [onTokenChange])

  useEffect(() => {
    onTokenExpireRef.current = onTokenExpire
  }, [onTokenExpire])

  const reset = useCallback(() => {
    if (
      typeof window !== "undefined" &&
      window.turnstile &&
      widgetIdRef.current != null
    ) {
      try {
        window.turnstile.reset(widgetIdRef.current)
      } catch { /* ignore */ }
    }
  }, [])

  useEffect(() => {
    if (formSubmitted) {
      reset()
      onTokenChangeRef.current && onTokenChangeRef.current("")
    }
  }, [formSubmitted, reset])

  useEffect(() => {
    unmountedRef.current = false
    let removed = false
    if (!siteKey || !containerRef.current) return
    if (
      typeof window !== "undefined" &&
      window.turnstile &&
      widgetIdRef.current != null
    ) {
      try {
        window.turnstile.remove(widgetIdRef.current)
      } catch { /* ignore */ }
      widgetIdRef.current = null
      containerRef.current.innerHTML = ""
    }
    waitForTurnstile()
      .then(() => {
        if (removed || unmountedRef.current) return
        if (!containerRef.current || !window.turnstile) return
        const id = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          size,
          theme,
          action,
          retry: "auto",
          "retry-interval": 5000,
          "refresh-expired": "auto",
          callback: (token) => {
            if (!unmountedRef.current && onTokenChangeRef.current) {
              onTokenChangeRef.current(token)
            }
          },
          "expired-callback": () => {
            if (!unmountedRef.current) {
              onTokenExpireRef.current && onTokenExpireRef.current()
              onTokenChangeRef.current && onTokenChangeRef.current("")
            }
          },
          "error-callback": () => {
            if (unmountedRef.current) return
            onTokenChangeRef.current && onTokenChangeRef.current("")
            if (retryingRef.current) return
            retryingRef.current = true
            setTimeout(() => {
              retryingRef.current = false
              if (unmountedRef.current || !window.turnstile || widgetIdRef.current == null) return
              try {
                window.turnstile.reset(widgetIdRef.current)
                if (size === "invisible") {
                  window.turnstile.execute(widgetIdRef.current, { async: true, action })
                }
              } catch { /* ignore */  }
            }, 5000)
          },
        })
        widgetIdRef.current = id
      })
      .catch(() => {
        onTokenChangeRef.current && onTokenChangeRef.current("")
      })
    return () => {
      removed = true
      unmountedRef.current = true
      if (
        typeof window !== "undefined" &&
        window.turnstile &&
        widgetIdRef.current != null
      ) {
        try {
          window.turnstile.remove(widgetIdRef.current)
        } catch { /* ignore */ }
        widgetIdRef.current = null
      }
    }
  }, [siteKey, size, theme, action])

  useImperativeHandle(ref, () => ({
    execute() {
      if (
        typeof window === "undefined" ||
        !window.turnstile ||
        widgetIdRef.current == null
      ) {
        return Promise.reject(new Error("turnstile_not_ready"))
      }
      return new Promise((resolve, reject) => {
        let settled = false
        const done = (fn) => {
          if (settled) return
          settled = true
          fn()
        }
        const to = setTimeout(() => done(() => reject(new Error("turnstile_execute_timeout"))), 8000)
        try {
          window.turnstile.execute(widgetIdRef.current, {
            async: true,
            action,
            callback: (token) => {
              clearTimeout(to)
              done(() => resolve(token))
            },
            "expired-callback": () => {
              clearTimeout(to)
              done(() => reject(new Error("expired")))
            },
            "error-callback": () => {
              clearTimeout(to)
              done(() => reject(new Error("error")))
            },
          })
        } catch (e) {
          clearTimeout(to)
          done(() => reject(e))
        }
      })
    },
    reset,
  }))

  return <div ref={containerRef} />
})

CustomTurnstile.propTypes = {
  siteKey: PropTypes.string,
  onTokenChange: PropTypes.func,
  onTokenExpire: PropTypes.func,
  formSubmitted: PropTypes.bool,
  size: PropTypes.oneOf(["normal", "compact", "flexible", "invisible"]),
  theme: PropTypes.oneOf(["light", "dark", "auto"]),
  action: PropTypes.string,
}

export default React.memo(CustomTurnstile)
